<template>
  <div>
    <div class="infoWindowHeader">
      <InfoWindowName
        v-if="showName"
        :info-window-name="infoWindowName"
        :placeholder="infoWindowNamePlaceholder"
        :editing="editing"
        data-testid="infoWindowName"
        @updateName="updateName"
      />
    </div>
    <div>
      <template v-if="!isComp">
        <DisplayFields
          :display-fields="localContent.displayFields"
          :editing="editing"
          data-testid="buildingDisplayFields"
          @addDisplayField="(...args) => handleEventAndForceUpdate('addDisplayField', args)"
          @updateDisplayField="(...args) => handleEventAndForceUpdate('updateDisplayField', args)"
          @deleteDisplayField="(...args) => handleEventAndForceUpdate('deleteDisplayField',args)"
        />
        <CompGroups
          :comps="localContent.comps"
          :editing="editing"
          :info-window-content="localContent"
          data-testid="compGroups"
          @addComp="(...args) => localContent.addComp(...args)"
          @addCompField="(...args) => handleEventAndForceUpdate('addCompDisplayField', args)"
          @updateCompField="(...args) => handleEventAndForceUpdate('updateCompDisplayField', args)"
          @updateCompName="(...args) => handleEventAndForceUpdate('updateCompName', args)"
          @deleteCompField="(...args) => handleEventAndForceUpdate('deleteCompDisplayField', args)"
          @deleteComp="(...args) => handleDeleteComp(...args)"
        />
        <LinkedDocuments
          :editing="editing"
          :is-linking-documents="isLinkingDocuments"
          :info-window-content="localContent"
          :icon-url="iconUrl"
          section-title="Portfolios"
          :config="linkingConfig"
          :hidden-link="isStaged"
          placeholder-text="Enter Portfolio Name"
          @stop-linking="isLinkingDocuments = false"
        />
        <AddCompButtons
          v-if="editing"
          :disabled-button-types="(isLinkingDocuments || localContent.id === newBuildingId) ? ['Portfolio'] : []"
          data-testid="addCompButtons"
          @addComp="(...args) => addCompAndForceSave(args)"
          @linkPortfolio="isLinkingDocuments = true"
        />
      </template>
      <template v-else>
        <v-expansion-panels
          v-model="expandedPanels"
          multiple
          variant="accordion"
        >
          <v-expansion-panel
            elevation="0"
            class="slim"
          >
            <v-expansion-panel-title class="subheading">
              {{ selectedComp.type }}
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <DisplayFields
                :display-fields="selectedComp?.displayFields ?? []"
                :editing="editing"
                data-testid="compDisplayFields"
                @addDisplayField="(...args) => localContent.addCompDisplayField(selectedComp, ...args)"
                @updateDisplayField="(...args) => localContent.updateCompDisplayField(selectedComp, ...args)"
                @deleteDisplayField="(...args) => localContent.deleteCompDisplayField(selectedComp, ...args)"
              />
            </v-expansion-panel-text>
          </v-expansion-panel>
          <v-expansion-panel
            elevation="0"
            class="slim"
          >
            <v-expansion-panel-title class="subheading">
              {{ localContent.title ?? 'Building' }}
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <DisplayFields
                :display-fields="localContent.displayFields"
                data-testid="buildingDisplayFields"
              />
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </template>
    </div>
    <div class="infoWindowFooter" />
  </div>
</template>

<script>
// Components
import InfoWindowName from './sections/Header/InfoWindowName.vue';
import DisplayFields from './DisplayFields/DisplayFields.vue';
import CompGroups from './DisplayFields/Comps/CompGroups.vue';
import AddCompButtons from './DisplayFields/Comps/AddCompButtons.vue';
import LinkedDocuments from './DisplayFields/LinkedDocuments/LinkedDocuments.vue';

// Tools & Services
import {mapMutations, mapActions, mapState} from 'vuex';
import {getCollection} from '@/lib/filters/filters.js';
import {currentUser, updateDocument} from '@/services/FirestoreService';
import {InfoWindowContent, competitiveBuildingToJSON} from '@/lib/classes/InfoWindowContent';
import {gmAPI, Markers} from '@/services/MapService';
import {
  createCompetitiveBuildingContent,
  duplicateComp,
  getAuditTrailToolbarBtn,
  getUnderwritingDashboardToolbarBtn,
  isEmptyBuilding,
  hasUnderwritingData,
  updateBuildingComps,
} from '@/lib/classes/InfoWindowContent/helpers/competitive';
import {FlagService, FLAGS} from '@/services/FlagService';
import {getCompPresentation} from '@/services/ContentPresentation';
import {NEW_BUILDING_ID} from '@/lib/symbols';
import {markerIconMap} from '@/services/MapService/Icons';
import {createLinkingConfig} from './DisplayFields/LinkedDocuments/Linking.js';
import {formatWithCommas} from '@/lib/ReusableFunctions/Formatters';
import {panToMarker} from '@/lib/map/panToMarker';
import {wrapError} from '@northpoint-development/labs-common-core';
import {
  reflectPortfolioBuildingDeletion,
  reflectPortfolioBuildingUndoDeletion,
  reflectCompDeletion,
  reflectCompUndoDeletion,
} from '@/lib/underwriting/adapters/scenario/syncDeletions';
import {findValidParentPortfolioId} from '@/lib/underwriting/adapters/scenario/parentPortfolio';

export default {
  name: 'CompetitiveBuildingInfoWindow',
  components: {
    InfoWindowName,
    DisplayFields,
    CompGroups,
    AddCompButtons,
    LinkedDocuments,
  },
  props: {
    infoWindowContent: {
      type: InfoWindowContent,
      required: true,
    },
  },
  emits: ['setInfoWindowToolbar'],
  data() {
    const iconUrl = markerIconMap.get('Blue-Portfolio-Flat')?.url;
    return {
      /** @type {boolean} */
      editing: false,
      /** @type {InfoWindowContent} */
      localContent: this.infoWindowContent,
      /** @type {[0,1]} used in comp mode to force open both comp info and building info panels */
      expandedPanels: [0, 1],
      newBuildingId: NEW_BUILDING_ID,
      selectedComp: null,
      isLinkingDocuments: false,
      iconUrl,
      linkingConfig: createLinkingConfig({
        firestoreCollection: 'beyondMaps-portfolios',
        filterCollection: 'competitivePortfolios',
        isOmInfoWindow: this.infoWindowContent.isStaged,
        fieldsToDisplay: ['Total Building Area', 'Sale Date', 'Sale Price', 'Price per sf', 'Going-In Cap Rate'],
        iwAction: this.createCompetitivePortfolioTab,
        linkAction: panToMarker,
        sectionHeaderSuffixField: 'Total Building Area',
        transformSuffix: (value) => value ? `${formatWithCommas(value)} SF` : '',
        onUnlink: this.infoWindowContent.metadata?.generatedByOm
          ? (portfolioId) => reflectPortfolioBuildingDeletion(this.localContent.id, portfolioId)
              .catch((err) => console.error(wrapError(err, 'Error reflecting portfolio unlink in underwriting scenarios')))
          : undefined,
        onUndoUnlink: this.infoWindowContent.metadata?.generatedByOm
          ? (portfolioId) => reflectPortfolioBuildingUndoDeletion(this.localContent.id, portfolioId)
              .catch((err) => console.error(wrapError(err, 'Error reflecting portfolio unlink undo in underwriting scenarios')))
          : undefined,
      }),
      // Centralized on InfoWindowContent so the view doesn't re-derive staged-ness from the type.
      isStaged: this.infoWindowContent.isStaged,
      isComp: this.infoWindowContent.type === 'competitiveBuildingComp',
    };
  },
  computed: {
    ...mapState(['compPickerMode']),
    isNew() {
      return this.localContent.id === NEW_BUILDING_ID;
    },
    infoWindowName() {
      if (this.isComp) return this.selectedComp?.name ?? this.localContent.title;
      return this.localContent.title;
    },
    infoWindowNamePlaceholder() {
      if (this.isComp) {
        const presentation = getCompPresentation(this.selectedComp?.type);
        return presentation.getPlaceholderText();
      }
      return 'Building Name';
    },
    showName() {
      return this.infoWindowName || this.editing;
    },
  },
  watch: {
    // watch localContent.id to see when the property is first saved.
    // When it is saved, set a local flag to delete the temp marker and add the property to the collection on unmount
    'localContent.id': {
      handler(newId, oldId) {
        if (newId !== NEW_BUILDING_ID && oldId === NEW_BUILDING_ID) {
          // Highlight does not persist, possibly related to [this issue](https://github.com/visgl/deck.gl/issues/8936)
          Markers.setHighlight('competitiveBuildings', newId, true);
          // when a new building is saved, the highlight and close actions need to use the newly generated id
          this.localContent.host.setHighlight = (state) => Markers.setHighlight('competitiveBuildings', newId, state);
          this.localContent.host.closeActions = () => Markers.setDraggable('competitiveBuildings', newId, false);
        }
      },
    },
  },
  beforeMount() {
    if (!this.isComp || this.localContent.selectedComp == null) {
      this.selectedComp = this.localContent.comps[0];
      return;
    }
    const comp = this.localContent.comps.find((comp) => comp.id === this.localContent.selectedComp);
    this.selectedComp = comp ?? this.localContent.comps[0];
  },
  async mounted() {
    if (this.isNew) {
      this.enterEditMode();
      // Save the new building immediately so that it has an id
      await this.localContent.updateSourceData();
      this.convertNewBuilding();
      return;
    }
    if (this.isStaged) {
      this.enterEditMode();
    } else {
      this.enterViewMode();
    }
  },
  beforeUnmount() {
    if (!this.isStaged && !this.isComp && this.editing &&
      isEmptyBuilding('competitiveBuildings', this.localContent)) {
      this.deleteCompetitiveBuilding({deleteMessage: 'New building has been cancelled'});
    }
  },
  methods: {
    ...mapMutations(['setSnack']),
    ...mapMutations('infowindow', ['updateInfoWindowContents']),
    ...mapActions('infowindow', ['closeInfoWindowTab', 'createCompetitiveBuildingTab', 'createCompetitivePortfolioTab', 'closeInfoWindow']),
    addCompAndForceSave(args) {
      this.localContent.addComp(...args);
    },
    /** @return {boolean} */
    addContentCopyBtn() {
      return !this.isComp && !this.isStaged;
    },
    /** @return {boolean} */
    addTrashBtn() {
      return !this.isComp && !this.isStaged;
    },
    /**
     * Forces an update to ensure what is saved locally matches what was sent to Firestore after
     * the function specified by `functionToCall` is invoked.
     * @param {string} functionToCall Name of the function `localContent` should invoke.
     * @param {any[]} args Display field arguments
     */
    handleEventAndForceUpdate(functionToCall, args) {
      // This fix is hacky and not ideal, but for some reason local state was not correctly reflecting when fields were
      // being updated and when comps were being added in building mode. This fixes the issue for now.
      this.localContent[functionToCall](...args);

      // updateBuildingComps will ensure the comps are updated
      const building = competitiveBuildingToJSON(this.localContent);
      // competitiveBuildingToJSON does not return any field that we update ourselves, so we need to set it here
      building.editedOn = {...this.localContent.metadata.editedOn};
      if (!this.isStaged) {
        updateBuildingComps(building);
      }
    },
    enterViewMode() {
      if (this.compPickerMode) {
        this.$emit('setInfoWindowToolbar', {toolbarOptions: []});
        this.editing = false;
        this.toggleDrag(false);
        return;
      }
      // TODO: InfoWindowContent could provide all buttons directly
      const toolbarOptions = [];
      if (!this.isStaged) {
        toolbarOptions.push([getAuditTrailToolbarBtn(
          this.isComp ? this.localContent.metadata.id : this.localContent.id,
          this.isComp ? 'competitiveBuildingComps' : 'competitiveBuildings',
          this.isComp ? this.selectedComp.id : null,
        )]);
      }
      if (FlagService?.getBooleanFlag(FLAGS.UNDERWRITING_DASHBOARD_ENABLED) &&
        hasUnderwritingData(this.localContent.metadata)) {
        // A comp's content id is the comp id; the parent building id lives on metadata
        const buildingId = this.isComp ? this.localContent.metadata.id : this.localContent.id;
        toolbarOptions.push([getUnderwritingDashboardToolbarBtn('building', buildingId)]);
      }
      if (this.addContentCopyBtn()) {
        toolbarOptions.push([{
          icon: 'ContentCopy',
          name: 'Duplicate Comp',
          action: 'duplicateMarker',
          callback: () => this.duplicateMarker(),
        }]);
      }
      toolbarOptions.push([
        {
          icon: 'Pencil',
          name: 'Edit Comp',
          action: 'enterEditMode',
          callback: () => this.enterEditMode(),
        },
      ]);
      this.$emit('setInfoWindowToolbar', {
        toolbarOptions,
      });
      this.editing = false;
      this.toggleDrag(false);
    },
    enterEditMode() {
      let toolbarOptions = [];
      if (this.localContent?.host?.getEditModeToolbar) {
        toolbarOptions = this.localContent.host.getEditModeToolbar();
      } else {
        // TODO: InfoWindowContent could provide all buttons directly
        if (!this.isStaged) {
          toolbarOptions.push([{
            icon: 'Check',
            name: 'Finish Editing',
            action: 'enterViewMode',
            callback: () => {
              if (!this.isComp && isEmptyBuilding('competitiveBuildings', this.localContent)) {
                this.deleteCompetitiveBuilding({deleteMessage: 'New building has been cancelled'});
                return;
              }
              this.enterViewMode();
            },
          }]);
        }
        if (this.addTrashBtn()) {
          toolbarOptions[0].unshift({
            icon: 'Trash',
            name: 'Delete Comp',
            action: 'delete',
            callback: () => {
              this.deleteCompetitiveBuilding();
            },
          });
        }
        if (this.addContentCopyBtn()) {
          toolbarOptions.unshift([{
            icon: 'ContentCopy',
            name: 'Duplicate Comp',
            action: 'duplicateMarker',
            callback: () => this.duplicateMarker(),
          }]);
        }
      }
      if (!this.isStaged) {
        toolbarOptions.unshift([getAuditTrailToolbarBtn(
          this.isComp ? this.localContent.metadata.id : this.localContent.id,
          this.isComp ? 'competitiveBuildingComps' : 'competitiveBuildings',
          this.isComp ? this.selectedComp.id : null,
        )]);
      }
      // Change the toolbar to the 'edit mode' toolbar
      this.$emit('setInfoWindowToolbar', {toolbarOptions});
      this.editing = true;
      this.toggleDrag(true);
    },
    async duplicateMarker() {
      const newMarkerContent = await duplicateComp(this.localContent, 'competitiveBuildings', createCompetitiveBuildingContent);
      this.editing = false;
      this.closeInfoWindow();
      this.updateInfoWindowContents({
        content: newMarkerContent,
      });
    },
    toggleDrag(canDrag) {
      if (this.isNew) return;
      // Comps are not draggable
      if (this.localContent.type === 'competitiveBuildingComp') return;
      // When the drag event finishes, we need to update the info window content location
      const dragEndCallback = (latLng) => {
        this.localContent.location = new gmAPI.LatLng(latLng);
        const building = competitiveBuildingToJSON(this.localContent);
        if (!this.isStaged) {
          updateBuildingComps(building);
        }
      };
      Markers.setDraggable(`${this.localContent.type}s`, this.localContent.id, canDrag, dragEndCallback);
    },
    /**
     * Selects the correct update function based on the current content type
     * @param {string} name
     */
    updateName(name) {
      if (this.isComp) {
        this.localContent.updateCompName(this.selectedComp, name);
      } else {
        this.localContent.updateName(name);
      }

      if (!this.isStaged) {
        // updateBuildingComps will ensure the comps are updated
        const building = competitiveBuildingToJSON(this.localContent);
        updateBuildingComps(building);
      }
    },
    /**
     * Handles deleting a building when the trash icon is pressed
     * @param {Object} options
     * @param {string} [options.deleteMessage] Message to show in the snackbar when the building is deleted
     */
    async deleteCompetitiveBuilding(options) {
      const docId = this.localContent.id;
      const localBuildingData = getCollection('competitiveBuildings').getItem(docId);
      const localCompData = this.localContent.comps?.map((comp) => {
        const compData = getCollection('competitiveBuildingComps').getItem(comp.id);
        return {
          ...compData,
          id: comp.id,
        };
      }) || [];

      // Resolve parent portfolio BEFORE the deletion write — the deletion removes the building from
      // the portfolio's linkedDocuments, so a post-deletion lookup returns null.
      const isOmBuilding = this.localContent.type === 'omCompetitiveBuilding' || this.localContent.metadata?.generatedByOm;
      const portfolioId = isOmBuilding ? await findValidParentPortfolioId(docId) : null;

      // The tab must be closed after retrieving the collection data for undo, because doing so removes the item
      // from the collection
      const iWTabIndex = await this.closeInfoWindowTab(this.localContent.id);

      // Remove the competitive building and comps from their collections
      getCollection('competitiveBuildings')?.removeItems({id: docId});
      getCollection('competitiveBuildingComps')?.removeItems(...this.localContent.comps);

      await this.updateLastEdited(true);
      if (isOmBuilding) {
        // Pass portfolioId (null for standalone) so the function handles both portfolio and standalone scenarios.
        reflectPortfolioBuildingDeletion(docId, portfolioId).catch((err) => console.error(wrapError(err, 'Error reflecting building deletion in underwriting scenarios')));
      }

      this.setSnack({
        message: options?.deleteMessage ? options.deleteMessage : 'Comp has been deleted',
        action: {
          text: 'Undo',
          callback: () => this.undoDeleteCompetitiveBuilding(localBuildingData, localCompData, iWTabIndex, portfolioId),
        },
      });
    },
    async undoDeleteCompetitiveBuilding(restoreBuildingData, restoreCompData, iWTabIndex, portfolioId) {
      try {
        if (!restoreBuildingData || !restoreBuildingData.id) {
          throw new Error('No building data to restore');
        }
        await this.updateLastEdited(false);
        if (portfolioId !== undefined) {
          // portfolioId may be null (standalone) or a string (portfolio) — both are handled.
          reflectPortfolioBuildingUndoDeletion(this.localContent.id, portfolioId)
            .catch((err) => console.error(wrapError(err, 'Error reflecting building deletion undo in underwriting scenarios')));
        }
        getCollection('competitiveBuildings').addItems(restoreBuildingData);
        getCollection('competitiveBuildingComps').addItems(...restoreCompData);

        const iwContent = competitiveBuildingToJSON(this.localContent);
        iwContent.editedOn = {
          timestamp: new Date(),
          name: currentUser().displayName,
          uid: currentUser().uid,
        };
        iwContent.geohash = this.localContent.metadata?.geohash;
        iwContent.markerData = {
          collection: 'competitiveBuildings',
          id: this.localContent.id,
          icon: iwContent.generatedByOm ? 'Darker-Blue-Balloon' : 'Blue-Balloon-Flat',
        };

        this.createCompetitiveBuildingTab({
          subject: {
            ...iwContent,
            firestoreCollection: 'beyondMaps-properties',
            filterCollection: 'competitiveBuildings',
          },
          insertIndex: iWTabIndex,
        });
      } catch (err) {
        console.error(wrapError(err, 'Error undoing delete'));
      }
    },
    async handleDeleteComp(compToDelete) {
      const compIndex = this.localContent.comps.findIndex((comp) => comp.id === compToDelete.id) || 0;
      const collectionCompData = getCollection('competitiveBuildingComps').getItem(compToDelete.id);

      // Remove the comp from the collection
      getCollection('competitiveBuildingComps')?.removeItems(compToDelete);

      // Remove the comp from the info window content
      this.localContent.deleteComp(compToDelete);

      if (!this.isStaged) {
        await this.updateLastEdited(false);
      }
      if (this.localContent.metadata?.generatedByOm) {
        reflectCompDeletion(this.localContent.id, compToDelete.id).catch((err) => console.error(wrapError(err, 'Error reflecting comp deletion in underwriting scenarios')));
      }

      this.setSnack({
        message: 'Comp has been deleted',
        action: {
          text: 'Undo',
          callback: () => this.undoDeleteComp(collectionCompData, compToDelete, compIndex),
        },
      });
    },
    async undoDeleteComp(collectionCompData, infoWindowCompData, compIndex) {
      try {
        // Add the comp back to the collection
        getCollection('competitiveBuildingComps').addItems(collectionCompData);

        // Add the comp back to the info window content
        this.localContent.addComp(infoWindowCompData, compIndex);

        if (!this.isStaged) {
          await this.updateLastEdited(false);
        }
        if (this.localContent.metadata?.generatedByOm) {
          reflectCompUndoDeletion(this.localContent.id, infoWindowCompData.id)
            .catch((err) => console.error(wrapError(err, 'Error reflecting comp deletion undo in underwriting scenarios')));
        }
      } catch (err) {
        console.error(wrapError(err, 'Error undoing comp delete'));
      }
    },
    /**
     * Converts a newly saved building from a temporary marker to a permanent marker and adds it to the filters
     * collection
     */
    convertNewBuilding() {
      // if this was a new building when the window was opened, but has now been saved to the db,
      // the building should be added to the collection to ensure the marker is persisted.
      getCollection('competitiveBuildings').removeItems({id: NEW_BUILDING_ID});
      Markers.delete('competitiveBuildings', NEW_BUILDING_ID);

      // Update the comps after the temporary marker has been removed
      const building = competitiveBuildingToJSON(this.localContent);
      if (!this.isStaged) {
        updateBuildingComps(building);
      }
    },
    /**
     * Updates the editedOn field in the database for the competitive building
     * @param {boolean} isDeleted - whether the building was deleted or not
     */
    async updateLastEdited(isDeleted = false) {
      const date = new Date();
      await updateDocument('beyondMaps-properties', this.localContent.id, {
        deleted: isDeleted ? date : null,
        editedOn: {
          timestamp: date,
          name: currentUser().displayName,
          uid: currentUser().uid,
        },
      });
    },
  },
};
</script>

<style scoped lang="scss">
.slim {
  :deep(.v-expansion-panel-title) {
    padding: 0;
    height: 22px;
  }
  :deep(.v-expansion-panel-title__overlay){
      opacity: 0;
  }
  :deep(.v-expansion-panel-text) {
    .v-expansion-panel-text__wrapper {
      padding: 0px 0px 16px;
    }
  }
}
</style>
